/**
 * Session-token status, published by the PowerSync connector each time it
 * fetches credentials. Surfaced on the Sync Status page so support can see at a
 * glance whether the auth token is fresh — the auth-expiry case that caused the
 * unauthenticated getWriteCheckpoint / "Last synced: Never" loop is otherwise
 * invisible.
 */

export interface SessionTokenStatus {
  /** JWT `exp` as a Date, or null when the token carried no/invalid expiry. */
  expiresAt: Date | null;
  /** When these credentials were last fetched. */
  fetchedAt: Date;
}

let latest: SessionTokenStatus | null = null;
const listeners = new Set<(s: SessionTokenStatus) => void>();

export function recordSessionTokenStatus(expiresAt: Date | null): void {
  latest = { expiresAt, fetchedAt: new Date() };
  for (const fn of listeners) {
    try {
      fn(latest);
    } catch {
      // never let a listener break credential fetching
    }
  }
}

export function getSessionTokenStatus(): SessionTokenStatus | null {
  return latest;
}

/** Subscribe to token-status updates. Returns an unsubscribe fn. */
export function onSessionTokenStatus(fn: (s: SessionTokenStatus) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
